'use client';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollAnimation from '@/components/ScrollAnimation';
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { faqSchema } from '@/lib/utils/structured-data';

const faqs = [
  {
    question: "How does Silker AI integrate with my existing application?",
    answer: "Silker runs where your app runs. Pick one of three paths: one line with our Node SDK (Next.js, Express), a Cloudflare Worker on your zone, or a Docker proxy in front of any backend. You can also start with a free scan of your live app first - no install required."
  },
  {
    question: "What types of threats does Silker AI detect?",
    answer: "Two things a generic WAF misses, plus the basics: prompt injection and jailbreaks on your AI/LLM routes, and sensitive data leaking out (API keys, secrets, PII). On top of that we cover OWASP basics like SQL injection, XSS, path traversal and malicious file uploads, plus rate limiting and bot abuse."
  },
  {
    question: "Does Silker AI slow down my application?",
    answer: "No. Detection runs in-process and adds minimal latency (typically under 10ms per request), and telemetry is sent asynchronously after the response - it never blocks your users. The self-hosted proxy option is a lightweight Node service built on the same detection core as the SDK."
  },
  {
    question: "Can I customize the security rules?",
    answer: "Yes. While Silker AI comes with smart defaults, you can configure custom rules, whitelist trusted IPs, adjust sensitivity levels, and define specific threat response actions through our dashboard. Enterprise plans include dedicated security consultations to tailor rules to your needs."
  },
  {
    question: "What happens when a threat is detected?",
    answer: "By default, Silker blocks the malicious request and logs the incident. You receive real-time alerts via Slack, email, or webhook. For some issue types we surface a concrete fix suggestion - you stay in control of what gets applied. All actions are configurable based on threat severity."
  },
  {
    question: "Is my data safe? Where is it stored?",
    answer: "Detection runs in your own runtime or edge, so your traffic doesn't pass through our servers. Request payloads aren't stored unless you explicitly enable forensics. Metadata (threat logs, analytics) is encrypted, and PII is redacted before anything is logged. We're built with GDPR in mind; SOC 2 Type II is in progress and not yet certified - we'll say so until it is."
  },
  {
    question: "What platforms and frameworks are supported?",
    answer: "The proxy works with any web application regardless of language or framework. Deploy via Docker, Kubernetes, or Cloudflare Workers. Your app can be Node.js, Python, Go, Java, PHP, Ruby, or anything else. We protect the traffic; we don't need to know your stack."
  },
  {
    question: "Does it work with Vercel?",
    answer: "Yes. Your app stays on Vercel. Run the Silker proxy on Railway, Fly.io, or Render (one Docker container). Set SILKER_TARGET to your Vercel URL (e.g. https://your-app.vercel.app). Point your custom domain to the proxy instead of Vercel. Traffic flows: user → proxy → your Vercel app. No code changes to your Vercel project."
  },
  {
    question: "Do you offer a free trial?",
    answer: "Yes! Our Starter plan is free forever for small projects (up to 10K requests/month). It includes real-time threat detection and basic auto-fixes. No credit card required. Upgrade to Pro or Enterprise anytime for advanced features and higher limits."
  },
  {
    question: "How does pricing work for multiple apps?",
    answer: "Pro is $39/month (or $31/month annually) and covers multiple apps. Business adds more apps and team features. Enterprise offers unlimited apps with custom pricing based on your total request volume and support needs. Contact us for a tailored quote. During beta you get 50% off your first 3 months."
  },
  {
    question: "What kind of support do you provide?",
    answer: "Starter includes community support (documentation, email). Pro gets priority email support with a 24-hour response target. Enterprise adds a dedicated channel and hands-on security guidance for incident response and compliance."
  },
  {
    question: "What is runtime application security?",
    answer: "Runtime application security (also called RASP - Runtime Application Self-Protection) means protecting a web application while it is actually running and serving traffic, rather than scanning it for vulnerabilities before or after deployment. A runtime security layer sits in the request path and inspects each request and response in real time, blocking attacks the moment they happen. Silker AI implements this as a drop-in reverse proxy or SDK - no code changes required."
  },
  {
    question: "What is the difference between a WAF and runtime application security?",
    answer: "A WAF (Web Application Firewall) sits at the network edge and filters traffic based on signatures and IP reputation before it reaches your app. Runtime application security (RASP) runs inside your application's execution environment and has full context about what the code is actually doing - making it effective against attacks that bypass WAF rules, such as prompt injection on AI routes, business-logic abuse, and sensitive data leaking in responses. Silker combines both approaches: edge filtering plus in-runtime inspection."
  },
  {
    question: "How do I secure an AI-built or vibe-coded app?",
    answer: "AI-generated code often skips authentication, exposes API keys in client bundles, and ships with Row-Level Security disabled on databases. Silker AI's reverse proxy protects any web app without requiring you to change the code - you point your domain at the Silker proxy and it inspects all traffic. It blocks SQL injection, XSS, prompt injection and detects secrets or PII leaking in API responses, making it a practical first security layer for apps built with Lovable, Bolt, Cursor, or any AI coding tool."
  }
];

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  
  const faqStructuredData = faqSchema(faqs);

  return (
    <div className="min-h-screen bg-black">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-rose-500/5 rounded-full blur-3xl"></div>
      </div>

      <Header />
      <ScrollAnimation />

      <main>
        <section className="relative pt-32 pb-24">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-20" data-scroll-animate="fade-up">
              <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
                <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
                FAQ
                <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
                <span className="text-white">Frequently asked</span>
                <br />
                <span className="bg-gradient-to-r from-neutral-200 via-neutral-400 to-neutral-600 bg-clip-text text-transparent">questions</span>
              </h1>
              <p className="text-base text-neutral-400 max-w-xl mx-auto">
                Everything you need to know about Silker AI
              </p>
            </div>

            <div className="space-y-3 mb-16" data-scroll-animate="fade-up">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="relative rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl overflow-hidden transition-all duration-300 hover:border-rose-500/20"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full text-left p-6 flex items-center justify-between gap-4 group cursor-pointer"
                  >
                    <span className="text-base font-medium text-white group-hover:text-rose-300 transition-colors">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-neutral-500 flex-shrink-0 transition-transform duration-300 ${
                        openIndex === index ? 'rotate-180 text-rose-400' : ''
                      }`}
                    />
                  </button>
                  
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 pb-6 text-sm text-neutral-400 leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center p-8 rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl" data-scroll-animate="fade-up">
              <h3 className="text-xl font-semibold mb-2 text-white">Still have questions?</h3>
              <p className="text-sm text-neutral-400 mb-4">
                Our team is here to help. Reach out and we'll get back to you within 24 hours.
              </p>
              <a
                href="mailto:support@silkerai.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-medium hover:bg-neutral-100 transition-colors"
              >
                Contact Support
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

